/**
 * WebMCP integration plugin — lifecycle management for connecting
 * WebMCP to the Cogo renderer.
 *
 * This module is NOT a React HOC or wrapper. It provides an imperative
 * integration object that can be used from hooks or directly in App.tsx.
 */

import { CogoToolRegistry } from "../core/tool-registry"
import { WebMCPStateProvider, type StateManagerLike } from "../core/state-provider"
import { installRuntimePolyfill, type CogoRuntimeRef } from "../adapters/runtime-adapter"

// ── Runtime type (matches CogoRuntime from cogo-react-renderer) ──

export interface CogoRuntimeLike {
  stateManager: StateManagerLike
  actionEngine?: unknown
  eventDispatcher?: unknown
  dataSourceManager?: unknown
  flowRegistry?: Record<string, unknown>
}

// ── App/Block types (matches cogo-types) ──

export interface CogoAppDefLike {
  appId: string
  name: string
  pages: Array<{ id: string; blocks: string[] }>
  [key: string]: unknown
}

export interface CogoBlockDefLike {
  manifest?: {
    name: string
    webmcp?: {
      expose: boolean
      tools: Record<string, { description: string; readOnly?: boolean }>
    }
    [key: string]: unknown
  }
  [key: string]: unknown
}

// ── Options ──

export interface WebMCPOptions {
  /** Whether to install window.__cogo_tools__ polyfill. Default: true */
  installPolyfill?: boolean
  /** Custom tool generators (Task #56 placeholder) */
  generators?: {
    generateFromManifest?: (
      manifest: CogoAppDefLike,
      blocks: Record<string, CogoBlockDefLike>,
      registry: CogoToolRegistry,
      stateProvider: WebMCPStateProvider,
    ) => void
    generateForPage?: (
      pageId: string,
      registry: CogoToolRegistry,
      stateProvider: WebMCPStateProvider,
    ) => void
  }
}

// ── Integration ──

export interface WebMCPIntegration {
  registry: CogoToolRegistry
  stateProvider: WebMCPStateProvider | null

  /** Called when CogoRuntime is ready (installs polyfill, creates state provider) */
  onRuntimeReady(runtime: CogoRuntimeLike): void

  /** Called when app/blocks are loaded (generates tools from DSL) */
  generateTools(
    manifest: CogoAppDefLike,
    blocks: Record<string, CogoBlockDefLike>,
  ): void

  /** Called on page navigation (re-generates page-specific tools) */
  onPageChange(pageId: string): void

  /** Cleanup polyfill and registry */
  dispose(): void
}

/**
 * Create a WebMCP integration instance.
 *
 * Manages the lifecycle of:
 * - CogoToolRegistry creation
 * - State provider binding when runtime is ready
 * - Runtime polyfill installation on window
 * - Tool generation from app manifest (delegates to generators when available)
 * - Cleanup on dispose
 */
export function createWebMCPIntegration(
  options: WebMCPOptions = {},
): WebMCPIntegration {
  const { installPolyfill = true, generators } = options

  const registry = new CogoToolRegistry()
  let stateProvider: WebMCPStateProvider | null = null
  let polyfillCleanup: (() => void) | null = null

  return {
    get registry() {
      return registry
    },

    get stateProvider() {
      return stateProvider
    },

    onRuntimeReady(runtime: CogoRuntimeLike): void {
      // Create state provider from runtime's state manager
      stateProvider = new WebMCPStateProvider(runtime.stateManager)

      // Build runtime ref for event dispatch / flow execution
      const runtimeRef: CogoRuntimeRef = {
        eventDispatcher: runtime.eventDispatcher as CogoRuntimeRef["eventDispatcher"],
        actionEngine: runtime.actionEngine as CogoRuntimeRef["actionEngine"],
        flowRegistry: runtime.flowRegistry as Record<string, unknown> | undefined,
      }

      // Install the window.__cogo_tools__ polyfill
      if (installPolyfill && stateProvider) {
        // Clean up any previous polyfill
        if (polyfillCleanup) {
          polyfillCleanup()
        }
        polyfillCleanup = installRuntimePolyfill(registry, stateProvider, runtimeRef)
      }
    },

    generateTools(
      manifest: CogoAppDefLike,
      blocks: Record<string, CogoBlockDefLike>,
    ): void {
      // Delegate to external generators if provided (Task #56)
      if (generators?.generateFromManifest && stateProvider) {
        try {
          generators.generateFromManifest(manifest, blocks, registry, stateProvider)
        } catch (err) {
          console.warn("WebMCP: generateFromManifest failed:", err)
        }
      }
    },

    onPageChange(pageId: string): void {
      // Delegate to external page-specific generators if provided (Task #56)
      if (generators?.generateForPage && stateProvider) {
        try {
          generators.generateForPage(pageId, registry, stateProvider)
        } catch (err) {
          console.warn("WebMCP: generateForPage failed:", err)
        }
      }
    },

    dispose(): void {
      // Remove polyfill from window
      if (polyfillCleanup) {
        polyfillCleanup()
        polyfillCleanup = null
      }

      // Clear all tools
      registry.clear()

      // Detach state provider
      stateProvider = null
    },
  }
}
